/*
 * @Author: alex
 * @Date: 2022-08-29 21:45:58
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2023-05-17 10:14:08
 * @Description:
 * Copyright (c) 2022 by piesat, All Rights Reserved.
 */
import type { FeatureLike } from 'ol/Feature'
import Feature from 'ol/Feature'
import { format } from 'ol/coordinate'
import type { Coordinate } from 'ol/coordinate'
import type { MapBrowserEvent } from 'ol'

import { GeoJSON } from 'ol/format'
import { BaseMapTool } from './mapToolBase'
import { error } from '../console'

interface MapIdentifyEvent {
  [_: string]: any
}

/**
 * 向gscp平台进行remote查询的配置参数
 */
export interface RemoteFeaturesFetchConfig {
  url: string
  remoteLayerId: string
}

/**
 * 独占识别图层
 */
interface ExclusiveIdentifyLayer {
  layerId: string
  callback: (feature: FeatureLike, coord: Coordinate) => void
  // 数据获取的配置
  fetchConfig?: RemoteFeaturesFetchConfig
}

const empty: ExclusiveIdentifyLayer = {
  layerId: '',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  callback: _feat => {
    // empty
  }
}

export default class MapIdentifyTool extends BaseMapTool<MapIdentifyEvent> {
  /**
   * 识别的目标图层和事件回调
   */
  #layerCallbacks: Map<
    string,
    Array<(feature: FeatureLike, coord: Coordinate) => void>
  > = new Map()

  #exclusiveLayer: ExclusiveIdentifyLayer = empty

  /**
   * remote 图层id 和 查询配置
   */
  #remoteLayers: Map<string, RemoteFeaturesFetchConfig> = new Map()

  /**
   * 添加一个识别图层
   * @param lyrId 图层id
   * @param callback 事件回调
   * @returns
   */
  addLayer(
    lyrId: string,
    callback: (feature: FeatureLike, coord: Coordinate) => void
  ): this {
    if (this.#layerCallbacks.has(lyrId)) {
      this.#layerCallbacks.get(lyrId)?.push(callback)
    } else {
      this.#layerCallbacks.set(lyrId, [callback])
    }
    return this
  }

  /**
   *
   * @param lyrId 地图中加载的图层id
   * @param fetchConfig
   * @param callback
   * @returns
   */
  addRemoteLayer(
    lyrId: string,
    fetchConfig: RemoteFeaturesFetchConfig,
    callback: (feature: FeatureLike, coord: Coordinate) => void
  ): this {
    this.#remoteLayers.set(lyrId, fetchConfig)
    if (this.#layerCallbacks.has(lyrId)) {
      this.#layerCallbacks.get(lyrId)?.push(callback)
    } else {
      this.#layerCallbacks.set(lyrId, [callback])
    }
    return this
  }

  /**
   * 设置独占图层
   * 仅仅当前图层可识别 其他均失效
   * @param lyrId  图层id
   * @param callback 回调
   */
  setExclusiveLayer(
    lyrId: string,
    callback: (feature: FeatureLike, coord: Coordinate) => void
  ): this {
    this.#exclusiveLayer = { layerId: lyrId, callback: callback }
    return this
  }

  /**
   * 设置remote layer 为独占图层
   * @param lyrId        地图图层id
   * @param fetchConfig  remote 数据获取配置
   * @param callback     获取数据后的回调
   * @returns
   */
  setRemoteExclusiveLayer(
    lyrId: string,
    fetchConfig: RemoteFeaturesFetchConfig,
    callback: (feature: FeatureLike, coord: Coordinate) => void
  ): this {
    this.#remoteLayers.set(lyrId, fetchConfig)
    this.#exclusiveLayer = { layerId: lyrId, callback: callback }
    return this
  }

  /**
   * 会清除该图层所有的事件回调
   * @param lyrId 图层id
   */
  removeLayer(lyrId: string) {
    if (
      this.#exclusiveLayer !== undefined &&
      this.#exclusiveLayer.layerId === lyrId
    ) {
      this.#exclusiveLayer = empty
    } else {
      this.#layerCallbacks.delete(lyrId)
      this.#remoteLayers.delete(lyrId)
    }
  }

  protected startInternal(): void {
    if (this.map !== undefined) {
      // 添加事件监听
      this.map.on('singleclick', e => this._singClickHandler(e))
    }
  }

  protected stopInternal(): void {
    // 移除事件监听
    this.map.un('singleclick', e => this._singClickHandler(e))
  }

  clear(): void {
    this.#layerCallbacks.clear()
    this.#remoteLayers.clear()
  }

  /**
   * 处理地图点击事件
   * @param e 地图点击事件
   */
  private _singClickHandler(e: MapBrowserEvent<any>) {
    let exclusiveLayer
    if (this.#exclusiveLayer !== empty) {
      const tmp = this.map
        .getAllLayers()
        .filter(
          lyr =>
            lyr.getVisible() && this.#exclusiveLayer?.layerId === lyr.get('id')
        )
      if (tmp.length > 0) {
        exclusiveLayer = tmp[0]
      }
    }
    const coord = e.coordinate
    if (exclusiveLayer !== undefined) {
      // 独占逻辑
      if (this.#remoteLayers.has(this.#exclusiveLayer.layerId)) {
        this._fetchRemoteFeature(
          coord,
          this.#remoteLayers.get(
            this.#exclusiveLayer.layerId
          ) as RemoteFeaturesFetchConfig
        )
          .then(feat => {
            if (feat !== null) {
              this.#layerCallbacks
                .get(this.#exclusiveLayer.layerId)
                ?.forEach(callback => callback.call(this, feat, coord))
            }
          })
          .catch(err => {
            error('identify remote layer error: ', err)
          })
      } else {
        exclusiveLayer
          .getFeatures(e.pixel)
          .then(fs => {
            if (fs.length > 0) {
              this.#exclusiveLayer?.callback.call(this, fs[0], coord)
            }
          })
          .catch(ex => {
            error('exclusive identify error:', ex)
          })
      }
    } else {
      this.map
        .getAllLayers()
        .filter(
          lyr =>
            lyr.getVisible() &&
            this.#layerCallbacks.has(lyr.get('id') as string)
        )
        .forEach(lyr => {
          //
          const lyrId = lyr.get('id') as string
          if (this.#remoteLayers.has(lyrId)) {
            // remote query
            this._fetchRemoteFeature(
              coord,
              this.#remoteLayers.get(lyrId) as RemoteFeaturesFetchConfig
            )
              .then(feat => {
                if (feat !== null) {
                  this.#layerCallbacks
                    .get(lyrId)
                    ?.forEach(callback => callback.call(this, feat, coord))
                }
              })
              .catch(err => {
                error('identify remote layer error: ', err)
              })
          } else {
            lyr
              .getFeatures(e.pixel)
              .then(fs => {
                if (fs.length > 0) {
                  this.#layerCallbacks
                    .get(lyrId)
                    ?.forEach(callback => callback.call(this, fs[0], coord))
                }
              })
              .catch(ex => {
                error('identify error:', ex)
              })
          }
        })
    }
  }

  /**
   * 远程方式获取识别的图形数据
   * @param coord 坐标点
   * @param fetchConfig 获取数据配置
   * @returns promise
   */
  private async _fetchRemoteFeature(
    coord: Coordinate,
    fetchConfig: RemoteFeaturesFetchConfig
  ): Promise<Feature | null> {
    const data: {
      code?: string
      msg: string
      [_: string]: any
    } = await fetch(fetchConfig.url, {
      method: 'POST',
      body: JSON.stringify({
        cqlExpression: `CONTAINS(the_geom,POINT(${format(
          coord,
          '{x} {y}',
          6
        )}))`,
        layerId: fetchConfig.remoteLayerId
      })
    }).then(res => res.json())
    if (data.code) {
      console.log('fetch feature err: ', data.msg)
    } else {
      const fs = new GeoJSON().readFeatures(data)
      return await Promise.resolve(fs[0])
    }
    return await Promise.resolve(null)
  }
}
